import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, money, errMsg } from "@/lib/api";
import { StatusPill, TrustChips, InlineLoader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import ActionDialog from "@/components/admin/ActionDialog";
import UnderReviewDialog from "@/components/admin/UnderReviewDialog";
import MatchManager from "@/components/MatchManager";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ArrowLeft, CheckCircle2, XCircle, HelpCircle, Flag, AlertTriangle, User, ShieldCheck, History, Eye,
} from "lucide-react";
import { toast } from "sonner";

export default function CampaignReview() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get(`/admin/campaigns/${campaignId}`).then((r) => setD(r.data)).catch((e) => setError(errMsg(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [campaignId]);

  const act = async (action, notes) => {
    await api.post(`/admin/campaigns/${campaignId}/${action}`, { notes });
    toast.success(`Campaign ${action.replace("-", " ")} done.`);
    if (action === "approve") navigate("/admin/reviews");
    else load();
  };
  const resolveFlag = async (fid, kind, notes) => { await api.post(`/admin/risk-flags/${fid}/${kind}`, { notes }); toast.success(`Flag ${kind}d.`); load(); };
  const resolveComplaint = async (cid, notes) => { await api.post(`/admin/complaints/${cid}/resolve`, { notes }); toast.success("Complaint resolved."); load(); };
  const putUnderReview = async (reasons, notes) => {
    await api.post(`/admin/campaigns/${campaignId}/under-review`, { reasons, notes });
    toast.success("Campaign is now under review. The organizer has been notified.");
    load();
  };

  if (error) return <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>;
  if (!d) return <InlineLoader label="Loading campaign" />;
  const c = d.campaign;

  return (
    <div className="mx-auto max-w-5xl">
      <Link to="/admin/reviews" className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-ff-medium hover:text-ff-red">
        <ArrowLeft className="h-4 w-4" /> Review queue
      </Link>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left: campaign */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-ff-border bg-white p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue">{c.title}</h1>
                <div className="mt-2 flex items-center gap-2"><StatusPill status={c.status} /><span className="text-sm text-ff-medium">{c.category}</span></div>
              </div>
            </div>
            {c.cover_image && <div className="mt-4 overflow-hidden rounded-xl border border-ff-border"><img src={c.cover_image} alt="" className="max-h-72 w-full object-cover" /></div>}
            <div className="mt-4"><TrustChips labels={c.trust_labels} /></div>
            <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-ff-dark">{c.story}</p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 text-sm">
              <Detail label="Beneficiary" value={c.beneficiary_name} />
              <Detail label="Relationship" value={c.relationship} />
              <Detail label="Intended use" value={c.intended_use} />
              <Detail label="Goal" value={c.goal_amount ? money(c.goal_amount) : "No fixed goal"} />
              <Detail label="Risk tier" value={c.risk_tier} />
              <Detail label="Truthfulness attested" value={c.truthfulness_attested ? "Yes" : "No"} />
            </div>
          </div>

          {/* Risk flags */}
          <Panel icon={Flag} title={`Risk flags (${d.risk_flags.length})`}>
            {d.risk_flags.length === 0 ? <Muted>No risk flags.</Muted> : d.risk_flags.map((f) => (
              <div key={f.id} className="flex items-start justify-between gap-3 rounded-lg border border-ff-border p-3" data-testid={`risk-flag-${f.id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${f.severity === "high" ? "bg-[#FEF2F2] text-ff-red" : "bg-[#FFF7ED] text-ff-amber"}`}>{f.severity}</span>
                    <span className="text-sm font-semibold text-ff-blue">{f.type}</span>
                    <StatusPill status={f.status} />
                  </div>
                  <div className="mt-1 text-xs text-ff-dark">{f.description}</div>
                </div>
                {f.status === "open" && (
                  <div className="flex shrink-0 gap-2">
                    <ActionDialog testid={`resolve-flag-${f.id}`} trigger={<Button size="sm" variant="outline" className="border-ff-green text-ff-green">Resolve</Button>}
                      title="Resolve risk flag" confirmLabel="Resolve" onConfirm={(n) => resolveFlag(f.id, "resolve", n)} />
                    <ActionDialog testid={`dismiss-flag-${f.id}`} trigger={<Button size="sm" variant="ghost">Dismiss</Button>}
                      title="Dismiss risk flag" confirmLabel="Dismiss" onConfirm={(n) => resolveFlag(f.id, "dismiss", n)} />
                  </div>
                )}
              </div>
            ))}
          </Panel>

          {/* Complaints */}
          <Panel icon={AlertTriangle} title={`Complaints (${d.complaints.length})`}>
            {d.complaints.length === 0 ? <Muted>No complaints.</Muted> : d.complaints.map((cp) => (
              <div key={cp.id} className="flex items-start justify-between gap-3 rounded-lg border border-ff-border p-3" data-testid={`complaint-${cp.id}`}>
                <div>
                  <div className="flex items-center gap-2"><span className="text-sm font-semibold text-ff-blue">{cp.reason}</span><StatusPill status={cp.status} /></div>
                  <div className="mt-1 text-xs text-ff-dark">{cp.description}</div>
                </div>
                {["open", "under_review"].includes(cp.status) && (
                  <ActionDialog testid={`resolve-complaint-${cp.id}`} trigger={<Button size="sm" variant="outline" className="border-ff-green text-ff-green">Resolve</Button>}
                    title="Resolve complaint" confirmLabel="Resolve" onConfirm={(n) => resolveComplaint(cp.id, n)} />
                )}
              </div>
            ))}
          </Panel>

          {/* Review history */}
          <Panel icon={History} title="Review history">
            {d.reviews.length === 0 ? <Muted>No actions yet.</Muted> : d.reviews.map((r) => (
              <div key={r.id} className="rounded-lg bg-[#F3F4F6] p-3 text-sm">
                <span className="font-semibold text-ff-blue">{r.action}</span>
                <span className="ml-2 text-xs text-ff-medium">{new Date(r.created_at).toLocaleString()}</span>
                {r.notes && <div className="mt-1 text-xs text-ff-dark">{r.notes}</div>}
              </div>
            ))}
          </Panel>
        </div>

        {/* Right: actions + organizer */}
        <div className="space-y-6">
          <div className="sticky top-6 space-y-6">
            <div className="rounded-2xl border border-ff-border bg-white p-6 shadow-sm">
              <h3 className="font-heading text-lg font-bold text-ff-blue">Decision</h3>
              <div className="mt-4 grid gap-2">
                <ActionDialog testid="approve-dialog" trigger={<Button className="w-full bg-ff-green text-white hover:bg-ff-green/90" data-testid="approve-button"><CheckCircle2 className="mr-2 h-4 w-4" /> Approve &amp; go live</Button>}
                  title="Approve campaign" description="This makes the campaign live and able to accept public donations." confirmLabel="Approve" onConfirm={(n) => act("approve", n)} />
                <ActionDialog testid="request-info-dialog" trigger={<Button variant="outline" className="w-full border-ff-amber text-ff-amber hover:bg-ff-amber hover:text-white" data-testid="request-info-button"><HelpCircle className="mr-2 h-4 w-4" /> Request more info</Button>}
                  title="Request more information" requireNotes confirmLabel="Send request" onConfirm={(n) => act("request-info", n)} />
                <UnderReviewDialog testid="under-review-dialog" onConfirm={putUnderReview}
                  trigger={<Button variant="outline" className="w-full border-ff-amber text-ff-amber hover:bg-ff-amber hover:text-white" data-testid="under-review-button"><Eye className="mr-2 h-4 w-4" /> Put under review</Button>} />
                <ActionDialog testid="reject-dialog" trigger={<Button variant="outline" className="w-full border-ff-red text-ff-red hover:bg-ff-red hover:text-white" data-testid="reject-button"><XCircle className="mr-2 h-4 w-4" /> Reject</Button>}
                  title="Reject campaign" description="This rejects the campaign." requireNotes destructive confirmLabel="Reject" onConfirm={(n) => act("reject", n)} />
              </div>
            </div>

            <div className="rounded-2xl border border-ff-border bg-white p-6 shadow-sm">
              <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-ff-blue"><User className="h-4 w-4" /> Organizer</h3>
              <div className="mt-3 space-y-2 text-sm">
                <Detail label="Name" value={d.organizer?.name} />
                <Detail label="Email" value={d.organizer?.email} />
                <Detail label="Phone verified" value={d.organizer_profile?.phone_verified ? "Yes" : "No"} />
                <Detail label="Stripe onboarding" value={d.organizer_profile?.stripe_onboarding_complete ? "Complete" : "Incomplete"} />
                <Detail label="Trust level" value={d.organizer_profile?.trust_level} />
              </div>
            </div>

            <MatchManager campaignId={c.id} match={c.match} onUpdate={load} />

            <div className="rounded-2xl border border-ff-border bg-[#F3F4F6] p-5">
              <div className="flex items-center gap-2 font-heading text-sm font-bold text-ff-blue"><ShieldCheck className="h-4 w-4 text-ff-green" /> Reminder</div>
              <p className="mt-2 text-xs text-ff-dark">Never claim campaigns are “fraud-proof” or “guaranteed safe.” Document every decision.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Panel({ icon: Icon, title, children }) {
  return (
    <div className="rounded-2xl border border-ff-border bg-white p-6 shadow-sm">
      <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-ff-blue"><Icon className="h-4 w-4" /> {title}</h3>
      <div className="mt-4 space-y-3">{children}</div>
    </div>
  );
}
function Detail({ label, value }) {
  return <div className="flex justify-between gap-3"><span className="text-ff-medium">{label}</span><span className="text-right font-semibold text-ff-blue">{value || "—"}</span></div>;
}
function Muted({ children }) { return <p className="text-sm text-ff-medium">{children}</p>; }
